/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type Transition from '@ember/routing/transition';
import { hash } from 'rsvp';
import ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';

export default class TopicsExplorerRoute extends Route {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;

  queryParams = {
    topicId: {
      refreshModel: false,
      replace: false,
    },
  };

  analytics = {
    section: 'automation',
    place: 'topics-explorer',
  };

  routeWhenNotOptedIn = 'apps.app.automation.fin-ai-agent.analyze.performance';

  get titleToken() {
    return this.intl.t('ai-agent.analyze.topics-explorer');
  }

  async beforeModel(transition: Transition) {
    if (!this.appService.app.canSeeAiTopics) {
      this.router.transitionTo(this.routeWhenNotOptedIn);
      return;
    }
    this.permissionsService.ensurePermissionWhenTransitioning('can_access_reporting', transition);
    let promises = [
      taskFor(this.reportingMetrics.setup).perform(),
      taskFor(this.reportingUnderlyingDataService.loadAttributeMappings).perform(
        'conversation_custom_fields#customer_experience_attributes',
        'conversation',
        true,
      ),
    ];
    if (!this.appService.app.isStandaloneApp) {
      promises.push(
        taskFor(this.reportingUnderlyingDataService.loadAttributeMappings).perform(
          'conversation.workflow_conversation_rating.workflow_id',
          'conversation',
          true,
        ),
      );
    }

    await Promise.all(promises);
    this.aiAgentSetupService.loadFinWorkflowsCount();
    this.aiAgentSetupService.loadSetupRulesets();
  }

  async model() {
    let conversationAttributeDescriptors = [];

    if (this.appService.app.canUseFeature('conversation_attributes')) {
      conversationAttributeDescriptors = ConversationAttributeDescriptor.peekAllAndMaybeLoad();
    }

    return hash({
      conversationAttributeDescriptors,
    });
  }
}
